import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const money = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
}

function drawHeader(doc, sale) {
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, W, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold").setFontSize(24).text("MOTOCRAFT", M, 38);
  doc.setFont("helvetica", "normal").setFontSize(10).text("DEALER MANAGEMENT", M, 56);
  doc.setFont("helvetica", "bold").setFontSize(10).text(`INVOICE  ${sale.invoice_number}`, W - M, 38, { align: "right" });
  doc.setFont("helvetica", "normal").text(fmtDate(sale.sale_date || sale.created_at), W - M, 54, { align: "right" });
  doc.setTextColor(15, 23, 42);
}

function drawParties(doc, buyer, car, y) {
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  doc.setFont("helvetica", "bold").setFontSize(10).text("BILL TO", M, y);
  doc.text("VEHICLE", W / 2 + 10, y);
  doc.setFont("helvetica", "normal").setFontSize(10);
  const buyerLines = [buyer?.name, buyer?.phone, buyer?.email, buyer?.address, buyer?.driver_license ? `DL: ${buyer.driver_license}` : ""].filter(Boolean);
  const carLines = [
    `${car?.year || ""} ${car?.make || ""} ${car?.model || ""} ${car?.trim || ""}`.trim(),
    car?.vin ? `VIN: ${car.vin}` : "",
    car?.color ? `Color: ${car.color}` : "",
    car?.mileage ? `Mileage: ${Number(car.mileage).toLocaleString()} mi` : "",
    car?.stock_number ? `Stock: ${car.stock_number}` : "",
  ].filter(Boolean);
  buyerLines.forEach((l, i) => doc.text(String(l), M, y + 16 + i * 14));
  carLines.forEach((l, i) => doc.text(String(l), W / 2 + 10, y + 16 + i * 14));
  return y + 16 + Math.max(buyerLines.length, carLines.length) * 14 + 16;
}

function drawChargesTable(doc, sale, total, startY) {
  const M = 40;
  const body = [["Vehicle Sale Price", money(sale.sale_price)]];
  if (Number(sale.discount)) body.push(["Discount", `- ${money(sale.discount)}`]);
  if (Number(sale.documentation_fee)) body.push(["Documentation Fee", money(sale.documentation_fee)]);
  if (Number(sale.registration_fee)) body.push(["Registration Fee", money(sale.registration_fee)]);
  if (Number(sale.other_fees)) body.push(["Other Fees", money(sale.other_fees)]);
  if (Number(sale.tax)) body.push(["Tax", money(sale.tax)]);

  autoTable(doc, {
    startY,
    head: [["DESCRIPTION", "AMOUNT"]],
    body,
    foot: [["TOTAL DUE", money(total)]],
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 8, lineColor: [203, 213, 225], lineWidth: 0.5 },
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: "bold", halign: "left" },
    columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
    footStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold" },
    margin: { left: M, right: M },
  });
  return doc.lastAutoTable.finalY + 24;
}

function drawPaymentBlock(doc, sale, balance, y) {
  const M = 40;
  doc.setFont("helvetica", "bold").setFontSize(10).text("PAYMENT", M, y);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text(`Method: ${(sale.payment_method || "—").toUpperCase()}`, M, y + 16);
  doc.text(`Received: ${money(sale.payment_received || 0)}`, M, y + 32);
  doc.setFont("helvetica", "bold").text(`Balance Due: ${money(balance)}`, M, y + 48);
}

function drawPnLBlock(doc, car, costs, gross_profit, net_profit, y) {
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(100, 116, 139).text("INTERNAL — NOT FOR BUYER", W - M, y, { align: "right" });
  doc.setTextColor(15, 23, 42).setFont("helvetica", "normal").setFontSize(9);
  doc.text(`Purchase: ${money(car?.purchase_price)}`, W - M, y + 14, { align: "right" });
  doc.text(`Auction Fees: ${money(costs?.auction_fees_total)}`, W - M, y + 26, { align: "right" });
  doc.text(`Repairs: ${money(costs?.repair_total)}`, W - M, y + 38, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.text(`Total Investment: ${money(costs?.total_investment)}`, W - M, y + 54, { align: "right" });
  const isProfit = gross_profit >= 0;
  doc.setTextColor(isProfit ? 4 : 220, isProfit ? 120 : 38, isProfit ? 87 : 38);
  doc.text(`Gross Profit: ${money(gross_profit)}`, W - M, y + 70, { align: "right" });
  doc.text(`Net Profit: ${money(net_profit)}`, W - M, y + 86, { align: "right" });
}

function drawSignatureFooter(doc, y) {
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  doc.setTextColor(15, 23, 42).setFont("helvetica", "normal").setFontSize(9);
  doc.line(M, y + 140, M + 220, y + 140);
  doc.text("Buyer Signature", M, y + 152);
  doc.line(W - M - 220, y + 140, W - M, y + 140);
  doc.text("Dealer Signature", W - M - 220, y + 152);
  doc.setTextColor(120, 120, 120).setFontSize(8);
  doc.text("Thank you for your business. Vehicle sold as-is unless otherwise noted in writing.", M, doc.internal.pageSize.getHeight() - 30);
}

export function downloadInvoicePDF(payload) {
  const { sale, vehicle: car, customer: buyer, costs, total, balance, gross_profit, net_profit } = payload;
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  drawHeader(doc, sale);
  const partiesEndY = drawParties(doc, buyer, car, 110);
  const tableEndY = drawChargesTable(doc, sale, total, partiesEndY);
  drawPaymentBlock(doc, sale, balance, tableEndY);
  drawPnLBlock(doc, car, costs, gross_profit, net_profit, tableEndY);
  drawSignatureFooter(doc, tableEndY);

  doc.save(`${sale.invoice_number}.pdf`);
}
